/**
 * League Manager Modal Components
 * Extracted from LeagueManagerTab.jsx for better maintainability
 */
import { useState, useEffect } from "react";
import { X, Loader2, Trash2, Plus, Image } from "lucide-react";
import { Button } from "../../ui/button";

const COLOR_PRESETS = [
  "#f97316", "#ef4444", "#22c55e", "#3b82f6", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f59e0b", "#6366f1", "#84cc16"
];

// League Modal
export const LeagueModal = ({ isOpen, onClose, onSave, league, saving }) => {
  const [formData, setFormData] = useState({
    name: "",
    abbreviation: "",
    sport: "Basketball",
    season: "",
    description: "",
    logo_url: "",
    color: "#f97316",
    show_in_menu: false,
    menu_order: 0,
    facebook_page_id: "",
    seo: {
      meta_title: "",
      meta_description: "",
      og_image_url: "",
      canonical_url: "",
      no_index: false
    }
  });

  useEffect(() => {
    if (league) {
      setFormData({
        name: league.name || "",
        abbreviation: league.abbreviation || "",
        sport: league.sport || "Basketball",
        season: league.season || "",
        description: league.description || "",
        logo_url: league.logo_url || "",
        color: league.color || "#f97316",
        show_in_menu: league.show_in_menu || false,
        menu_order: league.menu_order || 0,
        facebook_page_id: league.facebook_page_id || "",
        seo: league.seo || {
          meta_title: "",
          meta_description: "",
          og_image_url: "",
          canonical_url: "",
          no_index: false
        }
      });
    } else {
      setFormData({
        name: "",
        abbreviation: "",
        sport: "Basketball",
        season: "",
        description: "",
        logo_url: "",
        color: "#f97316",
        show_in_menu: false,
        menu_order: 0,
        facebook_page_id: "",
        seo: {
          meta_title: "",
          meta_description: "",
          og_image_url: "",
          canonical_url: "",
          no_index: false
        }
      });
    }
  }, [league, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <h3 className="text-xl font-bold text-white">
            {league ? "Edit League" : "Create New League"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-2">League Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                placeholder="e.g., East Texas Basketball League"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Abbreviation</label>
              <input
                type="text"
                value={formData.abbreviation}
                onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                placeholder="e.g., ETBL"
                maxLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Sport</label>
              <select
                value={formData.sport}
                onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                {["Basketball", "Football", "Soccer", "Baseball", "Softball", "Volleyball", "Hockey", "Other"].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Season</label>
              <input
                type="text"
                value={formData.season}
                onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                placeholder="e.g., 2024-25"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Menu Order</label>
              <input
                type="number"
                value={formData.menu_order}
                onChange={(e) => setFormData({ ...formData, menu_order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                min="0"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white h-24"
              placeholder="Brief description of the league..."
            />
          </div>

          {/* Branding */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Logo URL</label>
              <input
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Brand Color</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer bg-transparent"
                />
                <div className="flex gap-1 flex-wrap">
                  {COLOR_PRESETS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: c })}
                      className={`w-6 h-6 rounded ${formData.color === c ? "ring-2 ring-white" : ""}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Social */}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Facebook Page ID</label>
            <input
              type="text"
              value={formData.facebook_page_id}
              onChange={(e) => setFormData({ ...formData, facebook_page_id: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., YourLeagueFBPage"
            />
            <p className="text-xs text-zinc-500 mt-1">Used for social feed on league home page</p>
          </div>

          {/* Show in Menu */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.show_in_menu}
              onChange={(e) => setFormData({ ...formData, show_in_menu: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <span className="text-zinc-300">Show in navigation menu</span>
          </label>

          {/* SEO Section */}
          <div className="border-t border-zinc-800 pt-6">
            <h4 className="text-sm font-bold text-zinc-400 uppercase mb-4">SEO Settings</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Meta Title</label>
                <input
                  type="text"
                  value={formData.seo?.meta_title || ""}
                  onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, meta_title: e.target.value } })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="Page title for search engines"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Meta Description</label>
                <textarea
                  value={formData.seo?.meta_description || ""}
                  onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, meta_description: e.target.value } })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white h-20"
                  placeholder="Description for search results"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(formData)}
            disabled={!formData.name || saving}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {league ? "Update" : "Create"} League
          </Button>
        </div>
      </div>
    </div>
  );
};

// Conference Modal
export const ConferenceModal = ({ isOpen, onClose, onSave, conference, saving, leagueColor }) => {
  const [formData, setFormData] = useState({
    name: "",
    abbreviation: "",
    logo_url: "",
    color: leagueColor || "#f97316"
  });

  useEffect(() => {
    if (conference) {
      setFormData({
        name: conference.name || "",
        abbreviation: conference.abbreviation || "",
        logo_url: conference.logo_url || "",
        color: conference.color || leagueColor || "#f97316"
      });
    } else {
      setFormData({
        name: "",
        abbreviation: "",
        logo_url: "",
        color: leagueColor || "#f97316"
      });
    }
  }, [conference, isOpen, leagueColor]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-md">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">
            {conference ? "Edit Conference" : "Add Conference"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., Eastern Conference"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Abbreviation</label>
            <input
              type="text"
              value={formData.abbreviation}
              onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., EAST"
              maxLength={6}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Logo URL</label>
            <input
              type="url"
              value={formData.logo_url}
              onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer bg-transparent"
              />
              <div className="flex gap-1">
                {COLOR_PRESETS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: c })}
                    className={`w-5 h-5 rounded ${formData.color === c ? "ring-2 ring-white" : ""}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(formData)}
            disabled={!formData.name || saving}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {conference ? "Update" : "Add"} Conference
          </Button>
        </div>
      </div>
    </div>
  );
};

// Division Modal
export const DivisionModal = ({ isOpen, onClose, onSave, division, saving, conferences = [], leagueColor }) => {
  const [formData, setFormData] = useState({
    name: "",
    abbreviation: "",
    conference_id: "",
    logo_url: "",
    color: leagueColor || "#f97316"
  });

  useEffect(() => {
    if (division) {
      setFormData({
        name: division.name || "",
        abbreviation: division.abbreviation || "",
        conference_id: division.conference_id || "",
        logo_url: division.logo_url || "",
        color: division.color || leagueColor || "#f97316"
      });
    } else {
      setFormData({
        name: "",
        abbreviation: "",
        conference_id: "",
        logo_url: "",
        color: leagueColor || "#f97316"
      });
    }
  }, [division, isOpen, leagueColor]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-md">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">
            {division ? "Edit Division" : "Add Division"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., North Division"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Abbreviation</label>
            <input
              type="text"
              value={formData.abbreviation}
              onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., NORTH"
              maxLength={6}
            />
          </div>
          {conferences.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Conference</label>
              <select
                value={formData.conference_id}
                onChange={(e) => setFormData({ ...formData, conference_id: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="">No Conference (Standalone)</option>
                {conferences.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Logo URL</label>
            <input
              type="url"
              value={formData.logo_url}
              onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer bg-transparent"
              />
              <div className="flex gap-1">
                {COLOR_PRESETS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: c })}
                    className={`w-5 h-5 rounded ${formData.color === c ? "ring-2 ring-white" : ""}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(formData)}
            disabled={!formData.name || saving}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {division ? "Update" : "Add"} Division
          </Button>
        </div>
      </div>
    </div>
  );
};

// Playoff Modal
export const PlayoffModal = ({ isOpen, onClose, onSave, playoff, saving, conferences = [] }) => {
  const [formData, setFormData] = useState({
    name: "",
    team_count: 8,
    bracket_type: "single_sided",
    format: "single_elimination",
    is_active: true,
    seeding_enabled: false,
    conference_a_name: "",
    conference_b_name: "",
    conference_a_id: "",
    conference_b_id: "",
    custom_rounds: []
  });

  useEffect(() => {
    if (playoff) {
      setFormData({
        name: playoff.name || "",
        team_count: playoff.team_count || playoff.bracket_size || 8,
        bracket_type: playoff.bracket_type || "single_sided",
        format: playoff.format || "single_elimination",
        is_active: playoff.is_active ?? true,
        seeding_enabled: playoff.seeding_enabled ?? false,
        conference_a_name: playoff.conference_a_name || "",
        conference_b_name: playoff.conference_b_name || "",
        conference_a_id: playoff.conference_a_id || "",
        conference_b_id: playoff.conference_b_id || "",
        custom_rounds: playoff.custom_rounds || []
      });
    } else {
      setFormData({
        name: "",
        team_count: 8,
        bracket_type: "single_sided",
        format: "single_elimination",
        is_active: true,
        seeding_enabled: false,
        conference_a_name: "",
        conference_b_name: "",
        conference_a_id: "",
        conference_b_id: "",
        custom_rounds: []
      });
    }
  }, [playoff, isOpen]);

  const handleConferenceSelect = (side, conferenceId) => {
    const conference = conferences.find(c => c.id === conferenceId);
    if (side === "A") {
      setFormData({
        ...formData,
        conference_a_id: conferenceId,
        conference_a_name: conference?.name || formData.conference_a_name
      });
    } else {
      setFormData({
        ...formData,
        conference_b_id: conferenceId,
        conference_b_name: conference?.name || formData.conference_b_name
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <h3 className="text-xl font-bold text-white">
            {playoff ? "Edit Playoff Bracket" : "Create Playoff Bracket"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Bracket Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., 2025 Championship Playoffs"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Bracket Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, bracket_type: "single_sided" })}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.bracket_type === "single_sided"
                    ? "border-orange-500 bg-orange-500/10"
                    : "border-zinc-700 hover:border-zinc-600"
                }`}
              >
                <div className="font-medium text-white mb-1">Single Bracket</div>
                <div className="text-xs text-zinc-400">Standard single elimination</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, bracket_type: "double_sided" })}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.bracket_type === "double_sided"
                    ? "border-orange-500 bg-orange-500/10"
                    : "border-zinc-700 hover:border-zinc-600"
                }`}
              >
                <div className="font-medium text-white mb-1">Conference Championship</div>
                <div className="text-xs text-zinc-400">Two brackets → Championship</div>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              {formData.bracket_type === "double_sided" ? "Teams Per Conference" : "Number of Teams"}
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="2"
                max="64"
                value={formData.team_count}
                onChange={(e) => setFormData({ ...formData, team_count: parseInt(e.target.value) || 2 })}
                className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              />
              <div className="flex gap-1">
                {[4, 8, 16, 32].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setFormData({ ...formData, team_count: n })}
                    className={`px-3 py-2 rounded text-sm ${
                      formData.team_count === n 
                        ? "bg-orange-500 text-white" 
                        : "bg-zinc-800 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1">Enter any number - byes will be auto-calculated for non-power-of-2</p>
          </div>

          {/* Seeding Toggle */}
          <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.seeding_enabled || false}
                onChange={(e) => setFormData({ ...formData, seeding_enabled: e.target.checked })}
                className="sr-only peer"
                data-testid="seeding-toggle"
              />
              <div className="w-9 h-5 bg-zinc-700 peer-checked:bg-yellow-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
            </label>
            <div>
              <span className="text-sm text-white font-medium">Enable Seeding</span>
              <p className="text-[10px] text-zinc-500">Assign seed numbers (1, 2, 3...) to rank teams</p>
            </div>
          </div>

          {formData.bracket_type === "double_sided" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm font-medium text-white">Conference A (Left)</span>
                </div>
                {conferences.length > 0 && (
                  <select
                    value={formData.conference_a_id}
                    onChange={(e) => handleConferenceSelect("A", e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                  >
                    <option value="">-- Custom Name --</option>
                    {conferences.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}
                <input
                  type="text"
                  value={formData.conference_a_name}
                  onChange={(e) => setFormData({ ...formData, conference_a_name: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                  placeholder="e.g., AFC"
                />
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm font-medium text-white">Conference B (Right)</span>
                </div>
                {conferences.length > 0 && (
                  <select
                    value={formData.conference_b_id}
                    onChange={(e) => handleConferenceSelect("B", e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                  >
                    <option value="">-- Custom Name --</option>
                    {conferences.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}
                <input
                  type="text"
                  value={formData.conference_b_name}
                  onChange={(e) => setFormData({ ...formData, conference_b_name: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                  placeholder="e.g., NFC"
                />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <span className="text-zinc-300">Active</span>
          </label>
        </div>

        <div className="p-6 border-t border-zinc-800 flex justify-end gap-3 sticky bottom-0 bg-zinc-900">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(formData)}
            disabled={!formData.name || saving || (formData.bracket_type === "double_sided" && (!formData.conference_a_name || !formData.conference_b_name))}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {playoff ? "Update" : "Create"} Bracket
          </Button>
        </div>
      </div>
    </div>
  );
};

// Team Modal - Extracted from LeagueManagerTab.jsx
export const TeamModal = ({ isOpen, onClose, onSave, team, conferences, divisions, saving }) => {
  const [formData, setFormData] = useState({
    name: "",
    abbreviation: "",
    mascot: "",
    logo_url: "",
    color: "#333333",
    secondary_color: "",
    city: "",
    conference_id: "",
    division_entries: []
  });

  useEffect(() => {
    if (team) {
      setFormData({
        name: team.name || "",
        abbreviation: team.abbreviation || "",
        mascot: team.mascot || "",
        logo_url: team.logo_url || "",
        color: team.color || "#333333",
        secondary_color: team.secondary_color || "",
        city: team.city || "",
        conference_id: team.conference_id || "",
        division_entries: team.division_entries || []
      });
    } else {
      setFormData({
        name: "", abbreviation: "", mascot: "", logo_url: "",
        color: "#333333", secondary_color: "", city: "",
        conference_id: "", division_entries: []
      });
    }
  }, [team, isOpen]);

  const addDivisionEntry = () => {
    setFormData({
      ...formData,
      division_entries: [
        ...formData.division_entries,
        { id: "", division_id: "", display_name: `${formData.name || "Team"} - Division` }
      ]
    });
  };

  const updateDivisionEntry = (idx, field, value) => {
    const updated = [...formData.division_entries];
    updated[idx] = { ...updated[idx], [field]: value };
    setFormData({ ...formData, division_entries: updated });
  };

  const removeDivisionEntry = (idx) => {
    setFormData({
      ...formData,
      division_entries: formData.division_entries.filter((_, i) => i !== idx)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">
            {team ? "Edit Team" : "Add Team"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Team Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                placeholder="e.g., Dallas Eagles"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Abbreviation</label>
              <input
                type="text"
                value={formData.abbreviation}
                onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                placeholder="e.g., DAL"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Mascot</label>
              <input
                type="text"
                value={formData.mascot}
                onChange={(e) => setFormData({ ...formData, mascot: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                placeholder="e.g., Eagles"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                placeholder="e.g., Dallas, TX"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Logo URL</label>
            <input
              type="text"
              value={formData.logo_url}
              onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="https://..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Primary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="flex-1 px-2 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Secondary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.secondary_color || "#ffffff"}
                  onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.secondary_color}
                  onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                  className="flex-1 px-2 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Conference Assignment */}
          {conferences.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Conference</label>
              <select
                value={formData.conference_id}
                onChange={(e) => setFormData({ ...formData, conference_id: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="">No Conference</option>
                {conferences.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Division Entries */}
          {divisions.length > 0 && (
            <div className="border-t border-zinc-800 pt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-zinc-400">
                  Division Entries
                  <span className="text-xs text-zinc-600 ml-2">(e.g., Flag, JV, Varsity)</span>
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addDivisionEntry}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Entry
                </Button>
              </div>
              
              {formData.division_entries.length === 0 ? (
                <p className="text-sm text-zinc-600 text-center py-3">
                  No division entries. Add entries to appear in standings & brackets.
                </p>
              ) : (
                <div className="space-y-3">
                  {formData.division_entries.map((entry, idx) => {
                    const availableDivisions = formData.conference_id
                      ? divisions.filter(d => d.conference_id === formData.conference_id || !d.conference_id)
                      : divisions;
                    
                    return (
                      <div key={entry.id || idx} className="flex items-center gap-2 bg-zinc-800/50 rounded-lg p-3">
                        <div className="flex-1 space-y-2">
                          <select
                            value={entry.division_id || ""}
                            onChange={(e) => {
                              const divId = e.target.value;
                              const div = divisions.find(d => d.id === divId);
                              const autoName = div 
                                ? `${formData.name || "Team"} - ${div.name}`
                                : entry.display_name;
                              updateDivisionEntry(idx, "division_id", divId);
                              if (!entry.display_name || entry.display_name.includes(" - Division")) {
                                updateDivisionEntry(idx, "display_name", autoName);
                              }
                            }}
                            className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                          >
                            <option value="">Select Division *</option>
                            {availableDivisions.map(d => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                                {d.conference_id && conferences.find(c => c.id === d.conference_id)
                                  ? ` (${conferences.find(c => c.id === d.conference_id).name})`
                                  : ""}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={entry.display_name || ""}
                            onChange={(e) => updateDivisionEntry(idx, "display_name", e.target.value)}
                            className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                            placeholder="Display name (e.g., Tyler Lions - Flag)"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeDivisionEntry(idx)}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <p className="text-xs text-zinc-600 mt-2">
                Teams must have division entries to appear in standings and brackets.
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(formData)}
            disabled={!formData.name || saving}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {team ? "Update" : "Add"} Team
          </Button>
        </div>
      </div>
    </div>
  );
};
